import { FC, ReactNode, useEffect, useState } from 'react'
import classnames from 'classnames'
import { useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import { Alert, Layout } from 'antd'

import Box from '~/core/Box'
import Link from '~/core/Link'
import PlannedMaintenanceAlert from '~/apps/consumer/components/PlannedMaintenanceAlert'
import {
    getIsVendor,
    isImpersonating,
    getUserIdentity,
} from '~/redux-modules/identity'
import logger from '~/lib/logger'
import classList from '~/lib/classList'
import isFlagEnabled from '~/lib/isFlagEnabled'
import { getLocalStorageItem, setLocalStorageItem } from '~/lib/localStorage'
import { getenv } from '~/settings'
import TopNav from '~/apps/consumer/components/TopNav'
import { IdentityType } from '~/redux-modules/identity/type'

import NewSidebarNav from './NewSidebarNav'
import FixedLayoutContentWrapper from './FixedLayoutContentWrapper'
import styles from './styles.css'

const ADMIN_PAGE = 'admin/'
const QMR_PAGE = 'quote-model-revisions'

interface MainLayoutProps {
    children?: ReactNode
    content?: ReactNode
    background?: string
    scroll?: boolean
}

interface FixedLayoutProps extends MainLayoutProps {
    breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | undefined
    collapsedWidth?: number
    identity: IdentityType
    isImpersonatingUser?: boolean
    isVendor: boolean
    minWidth?: string
    sidebar?: string
    navTitle?: string
    trigger?: boolean
    showOpsNav?: boolean
    showCoronavirusAlert?: boolean
    noNavWrapper?: boolean
}

function WarningShroud({ children }: { children: ReactNode }) {
    return (
        <>
            <div className={styles.impersonating} />
            <div className={styles.impersonateEmail}>
                <span className={styles.impersonatedEmail}>{children}</span>
            </div>
        </>
    )
}

export const ConfigOverridesWarningShroud: FC = () => {
    const hasConfigOverrides = getenv('HAS_OVERRIDES')

    if (!hasConfigOverrides) {
        return null
    }

    return (
        <WarningShroud>
            <Link
                name="overrides"
                placement="warningshroud"
                to="/app/overrides"
            >
                overrides in effect
            </Link>
        </WarningShroud>
    )
}

const CoronavirusAlert = () => (
    <Alert
        showIcon={false}
        type="error"
        message="Please note that overseas lead times currently account for the impact of coronavirus. We are managing booked orders with partner capacity outside of affected areas to avoid any negative impact to our customers’ schedules."
        banner
    />
)

const MainLayout = (props: MainLayoutProps) => {
    const { background, content, children, scroll } = props

    return (
        <Layout
            className={classnames(
                styles.pageContent,
                scroll && styles.pageScroll,
                background === 'white' && styles.backgroundWhite,
            )}
        >
            <FixedLayoutContentWrapper content={content || children} />
        </Layout>
    )
}

const FixedLayout = (props: FixedLayoutProps) => {
    const {
        background,
        children,
        content,
        minWidth,
        scroll,
        sidebar,
        navTitle,
        showOpsNav,
        showCoronavirusAlert,
        noNavWrapper = false,
    } = props

    const location = useLocation()

    const navCollapsed = getLocalStorageItem('navCollapsed')

    const [collapsed, setCollapsed] = useState(navCollapsed === 'true')

    const toggleCollapsed = () => {
        setLocalStorageItem('navCollapsed', !collapsed)
        setCollapsed(!collapsed)
    }

    if (content && children) {
        logger.error(
            { content, children },
            'Cannot have children and content as props. FixedLayout can only render one.',
        )
    }

    const isShowPlannedMaintenanceBanner = () => {
        return !(
            location.pathname.includes(ADMIN_PAGE) ||
            location.pathname.includes(QMR_PAGE)
        )
    }

    useEffect(() => {
        if (!noNavWrapper) {
            classList(document.body).add(styles.pageReset)
        } else {
            classList(document.body).remove(styles.pageReset)
        }
        if (!sidebar) {
            classList(document.body).add(styles.noNavBar)
        } else {
            classList(document.body).remove(styles.noNavBar)
        }
        return () => {
            classList(document.body).remove(styles.pageReset)
            classList(document.body).remove(styles.noNavBar)
        }
    }, [noNavWrapper, sidebar])

    if (noNavWrapper) {
        return (
            <MainLayout {...{ background, content, scroll }}>
                {children}
            </MainLayout>
        )
    }

    return (
        <Layout
            className={classnames(
                styles.page,
                new Date().getMonth() === 5 ? styles.pridestyle : null,
            )}
            style={{ minWidth: minWidth || '1280px' }}
        >
            <ConfigOverridesWarningShroud />
            <Box height="100%" position="relative" direction="col">
                {isShowPlannedMaintenanceBanner() && (
                    <PlannedMaintenanceAlert />
                )}
                {showCoronavirusAlert && <CoronavirusAlert />}
                <TopNav
                    onlyShowTitle={navTitle}
                    showOpsNav={showOpsNav}
                    {...{
                        collapsed,
                        toggleCollapsed,
                    }}
                />
                <Box flex={1} style={{ overflowY: 'auto', flexGrow: 1 }}>
                    <NewSidebarNav {...{ collapsed, sidebar }} />
                    <MainLayout {...{ background, content, scroll }}>
                        {children}
                    </MainLayout>
                </Box>
            </Box>
        </Layout>
    )
}

export default connect(state => {
    const identity = getUserIdentity(state)

    return {
        identity,
        isVendor: getIsVendor(state),
        isImpersonatingUser: isImpersonating(state),
        showCoronavirusAlert: isFlagEnabled(
            identity,
            'SHOW_CORONAVIRUS_ALERT',
            'true',
        ),
    }
})(FixedLayout)
